import { InputAdornment, makeStyles, TextField } from "@material-ui/core";
import clsx from "clsx";
import { FieldConfig, useField } from "formik";
import { t } from "i18next";
import { useAppContext } from "qpr-base/context/AppCtxProvider";
import React, { useEffect, useState } from "react";
import { SimpleCell } from "../SubItems/formTable";
import usePropagateRef from "./../components/usePropagateRef";

interface Props extends FieldConfig {
  label?: string;
  multiline?: boolean;
  rows?: number;
  dir?: string;
  header?: string;
  placeholder?: string;
  disabled?: boolean;
  prefix?: string;
  suffix?: string;
  cell?: SimpleCell;
  disablePerformance?: boolean;
  loading?: boolean;
  className?: string;
  borderStyles?: string | undefined;
  maxLength?: number;
  labelStyle?: string;
  onFocus?:
    | React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>
    | undefined;
  onBlur?:
    | React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>
    | undefined;
  inTable?: boolean;
}

const useStyles = (rtl: boolean) =>
  makeStyles({
    container: { display: "flex", flexDirection: "column", width: "100%" },
    inputField: {
      boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
      borderRadius: "6px",
      height: "38px",
      fontStyle: "normal",
      fontWeight: 400,
      fontSize: "16px",
      "&.Mui-focused": {
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: "rgb(6, 156, 240)",
        },
      },
      lineHeight: "20px",
      direction: rtl ? "rtl" : "ltr",
      [rtl ? "paddingLeft" : "paddingRight"]: "28px",
    },
    textArea: {
      boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
      borderRadius: "6px",
      fontStyle: "normal",
      fontWeight: 400,
      fontSize: "16px",
      lineHeight: "20px",
      "&.Mui-focused": {
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: "rgb(6, 156, 240)",
        },
      },
    },
    notchedOutline: {
      borderColor: "#D1D5DB",
    },
    label: {
      fontFamily: "Dubai",
      fontWeight: 400,
      fontSize: "16px",
      lineHeight: "20px",
      color: "#374151",
      textAlign: rtl ? "right" : "left",
      marginTop: "1em",
      marginBottom: "0.375em",
    },
    disabled: {
      color: "#c9c9c9",
    },
    error: {
      fontSize: "14px",
      textAlign: rtl ? "right" : "left",
      marginRight: 0,
      marginLeft: 0,
    },
  })();
/**
 * use this whenever you want text field inside formik form
 * learn more about its structure from this link https://hackernoon.com/improving-formik-performance-when-its-slow-material-ui
 */
const FormikInputField = (props: Props) => {
  const ctx = useAppContext();
  const classes = useStyles(
    props.dir !== undefined ? props.dir === "rtl" : ctx.rtl
  );
  const [field, meta, helpers] = useField(props);
  const [fieldValue, setFieldValue] = useState<string | number>(field.value);
  usePropagateRef({ setFieldValue, name: props.name, value: field.value });
  useEffect(() => {
    if (meta.touched) {
      return;
    }
    if (field.value !== fieldValue) {
      setFieldValue(field.value);
    }
    // eslint-disable-next-line
  }, [field.value]);

  const onChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    setFieldValue(evt.target.value.slice(0, props.maxLength));
  };

  const onBlur = (evt: React.FocusEvent<HTMLInputElement>) => {
    const val = evt.target.value || "";

    window.setTimeout(() => {
      field.onChange({
        target: {
          name: props.name,
          value: props.type === "number" ? parseFloat(val) : val,
        },
      });
    }, 0);
  };

  const performanceProps = props.disablePerformance
    ? {
        ...field,
        value: props.loading ? "Loading..." : fieldValue,
      }
    : {
        ...field,
        value: props.loading ? "Loading..." : fieldValue,
        onChange,
        onBlur: (ev: any) => {
          onBlur(ev);
          if (props.onBlur) props.onBlur(ev);
        },
        onFocus: (ev: any) => {
          onBlur(ev);
          if (props.onFocus) props.onFocus(ev);
        },
      };
  function pickClassName() {
    if (props.className) {
      return props.className;
    }
    if (props.multiline) {
      return classes.textArea;
    }
    return classes.inputField;
  }
  return (
    <div className={classes.container}>
      <p
        className={clsx(
          classes.label,
          props.labelStyle,
          props.disabled ? classes.disabled : ""
        )}
      >
        {props.label}
      </p>

      <TextField
        autoComplete="off"
        onWheel={(event) => {
          (document.activeElement as any).blur();
        }}
        onScroll={(event) => {
          event.currentTarget.blur();
        }}
        InputProps={{
          classes: {
            root: pickClassName(),
            notchedOutline: props.borderStyles || classes.notchedOutline,
          },
          startAdornment: props.prefix && (
            <InputAdornment position="start">{props.prefix}</InputAdornment>
          ),
          endAdornment: (props.suffix || props.maxLength) && (
            <InputAdornment
              position="end"
              style={props.inTable ? {} : { width: "15px" }}
            >
              {props.suffix
                ? props.suffix
                : `${fieldValue.toString().length} /${props.maxLength}`}
            </InputAdornment>
          ),
        }}
        FormHelperTextProps={{
          classes: {
            root: classes.error,
          },
        }}
        fullWidth
        variant="outlined"
        {...field}
        {...props}
        {...performanceProps}
        dir="ltr"
        inputProps={{
          style: {
            textAlign: props.dir === "ltr" ? "left" : "right",
          },
        }}
        label=""
        error={meta.touched && Boolean(meta.error)}
        helperText={meta.touched && !!meta.error && t(meta.error)}
      />
    </div>
  );
};

export default React.memo(FormikInputField);

export function FormikInputFieldV2(props: Props) {
  const ctx = useAppContext();
  const classes = useStyles(ctx.rtl);
  const [field, meta, helper] = useField(props);
  const [value, setValue] = useState(field.value || "");

  function pickClassName() {
    if (props.className) {
      return props.className;
    }
    if (props.multiline) {
      return classes.textArea;
    }
    return classes.inputField;
  }

  const handleBlur = () => {
    helper.setTouched(true);
  };

  const onChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    setValue(evt.target.value.slice(0, props.maxLength));
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      field.onChange({
        target: {
          name: props.name,
          value: props.type === "number" ? parseFloat(value) : value,
        },
      });
    }, 200);
    return () => clearTimeout(timer);
  }, [value]);

  return (
    <div className={classes.container}>
      <p className={clsx(classes.label, props.labelStyle)}>{props.label}</p>
      <TextField
        autoComplete="off"
        onWheel={(event) => {
          (document.activeElement as any).blur();
        }}
        onScroll={(event) => {
          event.currentTarget.blur();
        }}
        InputProps={{
          classes: {
            root: pickClassName(),
            notchedOutline: props.borderStyles || classes.notchedOutline,
          },
          startAdornment: props.prefix && (
            <InputAdornment position="start">{props.prefix}</InputAdornment>
          ),
          endAdornment: (props.suffix || props.maxLength) && (
            <InputAdornment position="end" style={{ width: "15px" }}>
              {props.suffix
                ? props.suffix
                : `${value.length} /${props.maxLength}`}
            </InputAdornment>
          ),
        }}
        FormHelperTextProps={{
          classes: {
            root: classes.error,
          },
        }}
        {...props}
        fullWidth
        variant="outlined"
        value={value}
        onChange={onChange}
        onBlur={handleBlur}
        label=""
        error={meta?.touched && Boolean(meta?.error)}
        helperText={meta?.touched && t(meta?.error || "")}
      />
    </div>
  );
}
